import CalendarPublishedHTMLContextMenu from './CalendarPublishedHTMLContextMenu';
import CalendarPublishedICSContextMenu from './CalendarPublishedICSContextMenu';
import ColoredBulletOption from './ColoredBulletOption';
import PublishedCalendar from '../store/schema/PublishedCalendar';
import PublishingPermissionItemState from '../store/schema/PublishingPermissionItemState';
import { getCalendarSharingPermissions } from '../utils/CalendarSharingPermissionsUtils';
import { observer } from 'mobx-react';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { IPoint } from 'office-ui-fabric-react/lib/Utilities';
import 'owa-calendar-sharing/lib/orchestrators/resetCalendarPublishingPermissionLinksOrchestrator';
import getCalendarPublishingStore from 'owa-calendar-sharing/lib/store/publishingStore';
import { ObservableMap } from 'mobx';
import getAnchorForContextMenu from 'owa-positioning/lib/utils/getAnchorForContextMenu';
import * as React from 'react';
import {
    resetCalendarPublishingPermissionLinks,
    unpublishPublishedCalendar,
} from '../actions/calendarPublishing';
import strings from 'owa-strings/owa-options-strings';
import CalendarPublishingError from 'owa-calendar-sharing/lib/store/schema/CalendarPublishingError';
import { CalendarPublishingState } from 'owa-calendar-sharing/lib/store/schema/CalendarPublishingStore';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';

import { Link } from 'office-ui-fabric-react/lib/Link';
import { CommandButton } from 'office-ui-fabric-react/lib/Button';
import { getIsLightBaseTheme } from 'owa-theme';

let styles = require('./CalendarsPublishedComponent.scss');

export interface CalendarsPublishedComponentState {
    htmlAnchor: IPoint;
    icsAnchor: IPoint;
    htmlLink: string;
    icsLink: string;
}

export interface CalendarsPublishedComponentProps {
    onRetryInitialization: () => void;
}

@observer
export default class CalendarsPublishedComponent extends React.Component<
    CalendarsPublishedComponentProps,
    CalendarsPublishedComponentState
> {
    public constructor(props: any) {
        super(props);

        this.state = {
            htmlAnchor: null,
            icsAnchor: null,
            htmlLink: null,
            icsLink: null,
        };
    }

    public render() {
        const store = getCalendarPublishingStore();

        if (!store.viewState || !store.viewState.publishedCalendarsMap) {
            return null;
        }

        const { htmlAnchor, icsAnchor, htmlLink, icsLink } = this.state;

        let calendars: ObservableMap<string, PublishedCalendar> =
            store.viewState.publishedCalendarsMap;

        const isLightModeBaseTheme = getIsLightBaseTheme();
        const EMPTY_ERROR_SVG = isLightModeBaseTheme
            ? require('owa-empty-state/lib/svg/colored/loadingError.svg')
            : require('owa-empty-state/lib/svg/gray/loadingError.svg');

        return (
            <div>
                {store.viewState.errorState &&
                    store.viewState.errorState.error ==
                        CalendarPublishingError.InitialLoadFailure && (
                        <div className={styles.loadCalendarsErrorSection}>
                            <img
                                className={styles.loadCalendasErrorImage}
                                src={EMPTY_ERROR_SVG}
                                width="120px"
                                height="120px"
                            />

                            <div className={styles.loadCalendarsErrorMessage}>
                                <p>{strings.options_publishedCalendars_InitialLoadErrorMessage}</p>
                            </div>
                            <CommandButton
                                className={styles.loadCalendarsErrorTryagain}
                                onClick={this.props.onRetryInitialization}>
                                <span className={styles.loadCalendarsErrorTryAgainSpan}>
                                    {strings.options_publishedCalendars_TryAgainButton}
                                </span>
                            </CommandButton>
                        </div>
                    )}
                {(!store.viewState.errorState ||
                    store.viewState.errorState.error !=
                        CalendarPublishingError.InitialLoadFailure) &&
                    [...calendars.keys()].map((keyName: string) => {
                        let calendar = calendars.get(keyName);
                        const shouldRender =
                            calendar.itemStates &&
                            calendar.itemStates.filter(itemState => {
                                return (
                                    itemState &&
                                    itemState.CurrentDetailLevel &&
                                    itemState.CurrentDetailLevel != 'Disabled' &&
                                    (itemState.BrowseUrl || itemState.ICalUrl)
                                );
                            }).length > 0;
                        return shouldRender
                            ? this.renderPublishedCalendar(calendar, store.viewState)
                            : null;
                    })}

                <CalendarPublishedHTMLContextMenu
                    anchorPoint={htmlAnchor}
                    link={htmlLink}
                    onDismiss={this.onContextMenuDismiss}
                />
                <CalendarPublishedICSContextMenu
                    anchorPoint={icsAnchor}
                    link={icsLink}
                    onDismiss={this.onContextMenuDismiss}
                />
            </div>
        );
    }

    renderPublishedCalendar = (
        calendar: PublishedCalendar,
        viewState: CalendarPublishingState
    ): JSX.Element => {
        return (
            <div key={calendar.itemStates[0].folderId}>
                <h3 className={styles.calName}>
                    <ColoredBulletOption color={calendar.primaryColor} label={calendar.calName} />
                </h3>
                {calendar.itemStates.map(itemState => {
                    return (
                        <div className={styles.contentsWrapper}>
                            <div className={styles.detailsContainer}>
                                <div className={styles.currentDetailLevel}>
                                    {getCalendarSharingPermissions(itemState.CurrentDetailLevel)}
                                </div>
                                {itemState.isResetLinksPending ? (
                                    <div className={styles.resetPendingContainer}>
                                        <Spinner
                                            size={SpinnerSize.large}
                                            className={styles.largeSpinner}
                                        />
                                        <span className={styles.resetPendingSpan}>
                                            {strings.options_publishedCalendars_ResetLinksPending}
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        {itemState.BrowseUrl && (
                                            <div className={styles.publishURLsection}>
                                                <span className={styles.publishedLinkHeader}>
                                                    {strings.options_publishedCalendars_HTMLPrefix}
                                                </span>
                                                <Link
                                                    onClick={this.onHTMLContextMenu(
                                                        itemState.BrowseUrl
                                                    )}>
                                                    {itemState.BrowseUrl}
                                                </Link>
                                            </div>
                                        )}
                                        {itemState.ICalUrl && (
                                            <div className={styles.publishURLsection}>
                                                <span className={styles.publishedLinkHeader}>
                                                    {strings.options_publishedCalendars_ICSPrefix}
                                                </span>
                                                <Link
                                                    onClick={this.onICSContextMenu(
                                                        itemState.ICalUrl
                                                    )}>
                                                    {itemState.ICalUrl}
                                                </Link>
                                            </div>
                                        )}
                                    </>
                                )}
                                {viewState.errorState &&
                                    viewState.errorState.calendarErrors.get(itemState.folderId) ==
                                        CalendarPublishingError.UnpublishFailure && (
                                        <div className={styles.calendarsPublishedErrorSection}>
                                            <Icon
                                                className={styles.calendarsPublishedErrorIcon}
                                                iconName={ControlIcons.ErrorBadge}
                                            />
                                            <span className={styles.calendarsPublishedErrorMessage}>
                                                {
                                                    strings.options_publishedCalendars_UnpublishErrorMessage
                                                }
                                            </span>
                                        </div>
                                    )}
                                {viewState.errorState &&
                                    viewState.errorState.calendarErrors.get(itemState.folderId) ==
                                        CalendarPublishingError.ResetLinksFailure && (
                                        <div className={styles.calendarsPublishedErrorSection}>
                                            <Icon
                                                className={styles.calendarsPublishedErrorIcon}
                                                iconName={ControlIcons.ErrorBadge}
                                            />
                                            <span className={styles.calendarsPublishedErrorMessage}>
                                                {
                                                    strings.options_publishedCalendars_ResetLinksErrorMessage
                                                }
                                            </span>
                                        </div>
                                    )}
                            </div>
                            <div className={styles.linkButtons}>
                                {itemState.isUnpublishPending ? (
                                    <Spinner size={SpinnerSize.small} className={styles.spinner} />
                                ) : (
                                    <DefaultButton
                                        primary={false}
                                        disabled={false}
                                        text={strings.options_publishedCalendars_UnpublishButton}
                                        onClick={this.onUnpublishButtonClick(itemState)}
                                        className={styles.linkButton}
                                    />
                                )}
                                <DefaultButton
                                    primary={false}
                                    disabled={false}
                                    text={strings.options_publishedCalendars_ResetLinksButton}
                                    onClick={this.onResetLinksButtonClick(itemState)}
                                    className={styles.linkButton}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    onUnpublishButtonClick = (itemState: PublishingPermissionItemState): (() => void) => {
        return () => {
            unpublishPublishedCalendar(itemState.folderId, itemState.CurrentDetailLevel);
        };
    };

    onResetLinksButtonClick = (itemState: PublishingPermissionItemState): (() => void) => {
        return () => {
            resetCalendarPublishingPermissionLinks(
                itemState.folderId,
                itemState.CurrentDetailLevel
            );
        };
    };

    onHTMLContextMenu = (url: string) => {
        return (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            this.setState({
                htmlAnchor: getAnchorForContextMenu(event),
                htmlLink: url,
                icsAnchor: null,
                icsLink: null,
            });
        };
    };

    onICSContextMenu = (url: string) => {
        return (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            this.setState({
                htmlAnchor: null,
                htmlLink: null,
                icsAnchor: getAnchorForContextMenu(event),
                icsLink: url,
            });
        };
    };

    onContextMenuDismiss = () => {
        this.setState({
            htmlAnchor: null,
            icsAnchor: null,
        });
    };
}
